import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

// GCD via Euclidean
const gcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) [a, b] = [b, a % b]; return a; };
// LCM
const lcm = (a, b) => (a === 0 || b === 0) ? 0 : Math.abs(a * b) / gcd(a, b);

// All common factors of a set of positive integers
function commonFactors(nums) {
  if (!nums.length) return [];
  const g = nums.reduce((a, b) => gcd(a, b), nums[0]);
  const factors = [];
  for (let i = 1; i * i <= g; i++) {
    if (g % i === 0) {
      factors.push(i);
      if (i !== g / i) factors.push(g / i);
    }
  }
  return factors.sort((a, b) => a - b);
}

// Factor an integer into prime factorization
function primeFactorize(n) {
  n = Math.abs(n);
  const out = [];
  for (let p = 2; p * p <= n; p++) {
    while (n % p === 0) { out.push(p); n /= p; }
  }
  if (n > 1) out.push(n);
  return out;
}

export default function CommonFactorCalc() {
  const [text, setText] = useState('12, 18, 24');

  const r = useMemo(() => {
    const nums = text.split(/[\s,;]+/).map(s => s.trim()).filter(Boolean).map(Number).filter(n => Number.isInteger(n) && n > 0);
    if (nums.length < 2) return null;
    const cf = commonFactors(nums);
    const g = cf[cf.length - 1];
    const l = nums.reduce((a, b) => lcm(a, b));
    const factsPer = nums.map(n => ({ n, primes: primeFactorize(n) }));
    return { nums, commonFactors: cf, gcf: g, lcm: l, factsPer };
  }, [text]);

  return (
    <CalcShell look="cnet" title="Common Factor Calculator" subtitle="Find every common factor (divisor) shared by a list of positive integers, plus the greatest common factor (GCF) and least common multiple (LCM).">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field">
            <label>Integers (comma or space-separated)</label>
            <textarea rows={4} className="w-full font-mono text-sm p-2 border border-slate-300 rounded" value={text} onChange={e => setText(e.target.value)} data-testid="cf-input" />
          </div>
          <p className="text-xs text-slate-500">Enter at least two positive integers.</p>
        </div>
        <div>
          {r ? (
            <>
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 mb-3">
                <div className="text-xs text-emerald-700 font-semibold">ALL COMMON FACTORS</div>
                <div className="text-lg font-bold" data-testid="cf-all">{r.commonFactors.join(', ')}</div>
                <div className="text-xs text-slate-600 mt-1">Count: {r.commonFactors.length}</div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="p-4 rounded-xl bg-blue-50 border border-blue-200"><div className="text-xs text-blue-700 font-semibold">GREATEST COMMON FACTOR (GCF)</div><div className="text-2xl font-bold" data-testid="cf-gcf">{r.gcf}</div></div>
                <div className="p-4 rounded-xl bg-violet-50 border border-violet-200"><div className="text-xs text-violet-700 font-semibold">LEAST COMMON MULTIPLE (LCM)</div><div className="text-2xl font-bold" data-testid="cf-lcm">{r.lcm}</div></div>
              </div>
              <table className="data-table" data-testid="cf-primes">
                <thead><tr><th>Number</th><th>Prime factorization</th></tr></thead>
                <tbody>
                  {r.factsPer.map((f, i) => <tr key={i}><td>{f.n}</td><td className="font-mono">{f.primes.join(' × ')}</td></tr>)}
                </tbody>
              </table>
            </>
          ) : <div className="text-slate-500">Enter at least two positive integers.</div>}
        </div>
      </div>
      {r && <StepsPanel steps={[
        `Prime factorize each: ${r.factsPer.map(f => `${f.n} = ${f.primes.join('·') || 'prime'}`).join(', ')}`,
        `GCF = product of shared prime powers = <b>${r.gcf}</b>`,
        `LCM = product of highest prime powers = <b>${r.lcm}</b>`,
        `All common factors = divisors of GCF: <b>${r.commonFactors.join(', ')}</b>`,
      ]} />}
    </CalcShell>
  );
}
